import { supabase } from "@/integrations/supabase/client";

export interface InvoiceHistoryEntry {
  id: string;
  company_id: string;
  supplier_tax_id: string;
  /** NIF realmente leído del documento (cuando se contabilizó con un NIF genérico) */
  extracted_tax_id?: string | null;
  supplier_name: string;
  invoice_number: string | null;
  invoice_date: string | null; // YYYY-MM-DD u opcional
  base_amount: number | null;
  vat_amount: number | null;
  total_amount: number;
  source: "excel_import" | "vat_export" | "manual";
  notes: string | null;
  created_at: string;
}

export interface InvoiceHistoryInput {
  company_id: string;
  supplier_tax_id: string;
  extracted_tax_id?: string | null;
  supplier_name?: string;
  invoice_number?: string | null;
  invoice_date?: string | null;
  base_amount?: number | null;
  vat_amount?: number | null;
  total_amount: number;
  source: InvoiceHistoryEntry["source"];
  notes?: string | null;
}


export type DuplicateMatchLevel = 1 | 2 | 3 | 4;

export interface DuplicateMatch {
  level: DuplicateMatchLevel;
  confidence: "alta" | "alta-fecha" | "media" | "baja";
  reason: string;
  match: InvoiceHistoryEntry;
}

const TABLE = "invoice_history" as const;

// Normalizadores
const normalizeNif = (s: string): string => (s || "").replace(/[^a-zA-Z0-9]/g, "").toUpperCase().replace(/^ES(?=[A-Z0-9]{9}$)/, "");

/**
 * Normaliza un número de factura eliminando prefijos contables habituales
 * ("Su Fra Nº", "Factura", "Nº", "Núm", "Ref", "S/Fra", etc.) que aparecen
 * en históricos importados desde Excel pero NO en el dato extraído del PDF.
 */
const normalizeInvNum = (s: string): string => {
  if (!s) return "";
  let v = String(s).toUpperCase();
  // Quitar acentos / diacríticos (ej. "Nº" -> "NO", "Núm" -> "NUM")
  v = v.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  // Eliminar prefijos contables comunes (con o sin separadores)
  v = v.replace(/\b(SU\s*FRA|S\/?FRA|N\/?FRA|FRA|FACTURA|FACT|INVOICE|INV|REF(ERENCIA)?|NUMERO|NUM|N[ºO°\.]?)\b\.?\s*[:\-#]?\s*/g, "");
  // Quitar todo lo que no sea alfanumérico
  v = v.replace(/[^A-Z0-9]/g, "");
  // Si tiene más de 6 dígitos numéricos, nos quedamos con los últimos 6
  // (alineado con normalizeInvoiceNumber de Index.tsx y VatBook.tsx)
  const digits = v.replace(/\D/g, "");
  if (digits.length > 6) {
    return digits.slice(-6);
  }
  return v;
};

/**
 * Devuelve true si dos números de factura coinciden tras normalización.
 * Reglas estrictas para evitar falsos positivos:
 *  - Igualdad exacta tras normalización, O
 *  - Coincidencia por sufijo SOLO si el sufijo tiene ≥6 caracteres Y
 *    contiene al menos un carácter no numérico O al menos 5 dígitos significativos
 *    (evita que "001", "2024" o "26" matcheen como sufijo común).
 */
const invoiceNumbersMatch = (a: string, b: string): boolean => {
  const na = normalizeInvNum(a);
  const nb = normalizeInvNum(b);
  if (!na || !nb) return false;
  if (na === nb) return true;

  // Sufijo: solo aceptamos si el más corto es muy específico (≥6 caracteres)
  // para tolerar "SUFRANO168SL26" vs "168SL26" pero NO "999026" vs "26".
  const shorter = na.length <= nb.length ? na : nb;
  const longer = na.length > nb.length ? na : nb;
  if (shorter.length < 6) return false;
  if (!longer.endsWith(shorter)) return false;

  // Si todo el sufijo son dígitos, exigimos que no sea solo ceros/año habitual
  const allDigits = /^\d+$/.test(shorter);
  if (allDigits) {
    // Rechazar sufijos que sean solo año + secuencial corto repetido
    // Exigimos ≥7 dígitos para sufijos puramente numéricos
    if (shorter.length < 7) return false;
  }
  return true;
};

const AMOUNT_TOLERANCE = 0.01; // €
const DATE_WINDOW_DAYS = 30;   // ventana para nivel 4

const daysBetween = (a?: string | null, b?: string | null): number => {
  if (!a || !b) return Infinity;
  const da = new Date(a + "T00:00:00").getTime();
  const db = new Date(b + "T00:00:00").getTime();
  if (Number.isNaN(da) || Number.isNaN(db)) return Infinity;
  return Math.abs(Math.round((da - db) / 86_400_000));
};

const amountsMatch = (a?: number | null, b?: number | null): boolean => {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(Number(a) - Number(b)) <= AMOUNT_TOLERANCE;
};

/** NIF ficticios usados para "proveedores varios" */
export const GENERIC_TAX_IDS = new Set(["00000000T"]);

export const isGenericTaxId = (s?: string | null): boolean =>
  GENERIC_TAX_IDS.has(normalizeNif(s || ""));

/**
 * Carga el histórico de una empresa filtrado por NIF. Compara tanto el NIF
 * contable como el NIF realmente extraído del documento, de modo que una
 * factura registrada bajo un NIF genérico (proveedores varios) siga siendo
 * detectable al volver a subirla con su NIF real.
 */
export const loadHistoryByNif = async (
  companyId: string,
  supplierTaxId: string,
  extractedTaxId?: string | null,
): Promise<InvoiceHistoryEntry[]> => {
  const nifs = new Set(
    [supplierTaxId, extractedTaxId].map((v) => normalizeNif(v || "")).filter(Boolean),
  );
  if (!companyId || nifs.size === 0) return [];
  const { data, error } = await (supabase as any)
    .from(TABLE)
    .select("*")
    .eq("company_id", companyId);
  if (error) {
    console.error("loadHistoryByNif error:", error);
    return [];
  }
  return (data as InvoiceHistoryEntry[]).filter(
    (r) =>
      nifs.has(normalizeNif(r.supplier_tax_id)) ||
      (!!r.extracted_tax_id && nifs.has(normalizeNif(r.extracted_tax_id))),
  );
};

/**
 * Cotejo en cascada (la fecha es opcional en el histórico migrado):
 *  Nivel 1 — NIF + Nº factura + Base imponible (±0,01€)            → confianza ALTA
 *  Nivel 2 — NIF + Nº factura (cuando no hay base que cotejar)     → confianza ALTA
 *  Nivel 3 — NIF + Fecha exacta + Total (±0,01€)                   → confianza ALTA-FECHA
 *  Nivel 4 — NIF + Total (±0,01€) en ventana ±30 días              → confianza MEDIA
 *
 * El NIF puede coincidir por el NIF contable o por el NIF extraído del
 * documento (facturas llevadas a una cuenta genérica de proveedores varios).
 */
export const matchAgainstHistory = (
  candidate: {
    supplier_tax_id: string;
    extracted_tax_id?: string | null;
    invoice_number?: string | null;
    invoice_date?: string | null;
    base_amount?: number | null;
    total_amount: number;
  },
  history: InvoiceHistoryEntry[],
): DuplicateMatch | null => {
  const candNifs = new Set(
    [candidate.supplier_tax_id, candidate.extracted_tax_id]
      .map((v) => normalizeNif(v || ""))
      .filter(Boolean),
  );
  const nif = normalizeNif(candidate.supplier_tax_id);
  if (candNifs.size === 0 || !Number.isFinite(candidate.total_amount)) return null;

  const sameNif = history.filter(
    (h) =>
      candNifs.has(normalizeNif(h.supplier_tax_id)) ||
      (!!h.extracted_tax_id && candNifs.has(normalizeNif(h.extracted_tax_id))),
  );
  if (sameNif.length === 0) return null;
  /** Sufijo informativo cuando el registro histórico se contabilizó como proveedores varios */
  const genericNote = (h: InvoiceHistoryEntry): string =>
    isGenericTaxId(h.supplier_tax_id) && normalizeNif(h.supplier_tax_id) !== nif
      ? ` · registrada como proveedores varios (${normalizeNif(h.supplier_tax_id)})`
      : "";

  const candInv = candidate.invoice_number || "";
  const candBase = Number(candidate.base_amount);
  const total = Number(candidate.total_amount);

  // Importe candidato (base preferente; si no, total)
  const candAmount = Number.isFinite(candBase) && candBase > 0 ? candBase : total;


  // Nivel 1: NIF + Nº factura + importe (base candidata vs base histórica,
  // o bien base candidata vs total histórico, o total vs base histórica).
  if (candInv) {
    const sameInv = sameNif.filter(
      (h) => h.invoice_number && invoiceNumbersMatch(h.invoice_number, candInv),
    );

    // Coincidencia exacta de importes
    const m1 = sameInv.find(
      (h) =>
        amountsMatch(h.base_amount, candAmount) ||
        amountsMatch(h.total_amount, candAmount) ||
        (Number.isFinite(total) && total > 0 &&
          (amountsMatch(h.base_amount, total) || amountsMatch(h.total_amount, total))),
    );
    if (m1) {
      const ref = amountsMatch(m1.base_amount, candAmount) || amountsMatch(m1.base_amount, total)
        ? `base ${Number(m1.base_amount).toFixed(2)}€`
        : `total ${Number(m1.total_amount).toFixed(2)}€`;
      return {
        level: 1,
        confidence: "alta",
        reason: `NIF + Nº factura (${m1.invoice_number}) + ${ref} coinciden${genericNote(m1)}`,
        match: m1,
      };
    }

    // Nivel 2: NIF + Nº factura (sin importe fiable o sin importe en histórico)
    const m2 = sameInv.find((h) => {
      const noHistAmount = (!h.base_amount || Number(h.base_amount) === 0) &&
                           (!h.total_amount || Number(h.total_amount) === 0);
      return noHistAmount;
    }) ?? sameInv[0];

    if (m2) {
      return {
        level: 2,
        confidence: "alta",
        reason: `NIF + Nº factura coinciden (${m2.invoice_number})${genericNote(m2)}`,
        match: m2,
      };
    }
  }

  // Niveles 3 y 4 (NIF + fecha/total sin nº factura) DESACTIVADOS:
  // generaban demasiados falsos positivos en facturas recurrentes
  // (leasings, suministros, cuotas mensuales con mismo importe).
  // La detección de duplicados exige siempre coincidencia de Nº de factura.

  return null;
};

/**
 * Inserta varias entradas. Si una colisiona con (company_id, NIF, nº factura)
 * por el índice único, la ignoramos silenciosamente.
 */
export const insertHistoryEntries = async (entries: InvoiceHistoryInput[]): Promise<{
  inserted: number;
  skipped: number;
  errors: string[];
}> => {
  if (entries.length === 0) return { inserted: 0, skipped: 0, errors: [] };

  let inserted = 0;
  let skipped = 0;
  const errors: string[] = [];

  // Lotes pequeños para reducir riesgo de timeouts y aislar fallos
  const BATCH = 50;
  for (let i = 0; i < entries.length; i += BATCH) {
    const chunk = entries.slice(i, i + BATCH).map((e) => ({
      company_id: e.company_id,
      supplier_tax_id: normalizeNif(e.supplier_tax_id),
      extracted_tax_id: e.extracted_tax_id ? normalizeNif(e.extracted_tax_id) : null,
      supplier_name: e.supplier_name || "",
      invoice_number: e.invoice_number ? e.invoice_number.trim() : null,
      invoice_date: e.invoice_date,
      base_amount: e.base_amount ?? null,
      vat_amount: e.vat_amount ?? null,
      total_amount: e.total_amount,
      source: e.source,
      notes: e.notes ?? null,
    }));

    const { data, error } = await (supabase as any)
      .from(TABLE)
      .insert(chunk)
      .select("id");

    if (!error) {
      inserted += (data as any[])?.length ?? chunk.length;
      continue;
    }

    // Cualquier error en el insert masivo (no solo 23505) provoca rollback del
    // batch en Postgres. Reintentamos fila a fila para no perder ninguna.
    console.warn(
      `[invoice_history] batch falló (${(error as any).code || "?"}): ${error.message}. Reintentando ${chunk.length} filas individualmente.`,
    );
    for (const row of chunk) {
      try {
        const { error: e2 } = await (supabase as any).from(TABLE).insert(row);
        if (!e2) {
          inserted += 1;
        } else if ((e2 as any).code === "23505") {
          skipped += 1;
        } else {
          errors.push(`${row.supplier_tax_id} ${row.invoice_number ?? ""}: ${e2.message}`);
          console.error("[invoice_history] fila no insertada:", row, e2);
        }
      } catch (ex: any) {
        errors.push(`${row.supplier_tax_id} ${row.invoice_number ?? ""}: ${ex?.message || "excepción"}`);
        console.error("[invoice_history] excepción insertando fila:", row, ex);
      }
    }
  }

  return { inserted, skipped, errors };
};

export const deleteHistoryEntry = async (id: string): Promise<boolean> => {
  const { error } = await (supabase as any).from(TABLE).delete().eq("id", id);
  if (error) {
    console.error("deleteHistoryEntry error:", error);
    return false;
  }
  return true;
};

export const listHistory = async (
  companyId: string,
  search?: string,
  limit = 500,
): Promise<InvoiceHistoryEntry[]> => {
  if (!companyId) return [];
  let q: any = (supabase as any)
    .from(TABLE)
    .select("*")
    .eq("company_id", companyId)
    .order("invoice_date", { ascending: false })
    .limit(limit);
  if (search && search.trim()) {
    const s = `%${search.trim()}%`;
    q = q.or(`supplier_tax_id.ilike.${s},supplier_name.ilike.${s},invoice_number.ilike.${s}`);
  }
  const { data, error } = await q;
  if (error) {
    console.error("listHistory error:", error);
    return [];
  }
  return data as InvoiceHistoryEntry[];
};

export const countHistory = async (companyId: string): Promise<number> => {
  if (!companyId) return 0;
  const { count, error } = await (supabase as any)
    .from(TABLE)
    .select("*", { count: "exact", head: true })
    .eq("company_id", companyId);
  if (error) return 0;
  return count ?? 0;
};
